import React, { createContext, useContext, useState, useEffect } from 'react';
import { logger } from "../utils/logger";

export interface UXPreferencesContextValue {
  textSize: string;
  setTextSize: React.Dispatch<React.SetStateAction<string>>;
  cycleTextSize: () => void;
  density: string;
  setDensity: React.Dispatch<React.SetStateAction<string>>;
  toggleDensity: () => void;
  getTextSizeClass: (customMapping?: Record<string, string> | null) => string;
  getDensityClasses: () => { padding: string; gap: string; spacing: string };
  TEXT_SIZE_OPTIONS: string[];
  DENSITY_OPTIONS: string[];
}

export const UXPreferencesContext = createContext<UXPreferencesContextValue | null>(null);

const TEXT_SIZE_OPTIONS = ['small', 'medium', 'large'];
const DENSITY_OPTIONS = ['compact', 'comfortable'];

const TEXT_SIZE_CLASSES = {
  small: 'text-sm',
  medium: 'text-base',
  large: 'text-lg',
};

const DENSITY_CLASSES = {
  compact: {
    padding: 'py-1 px-2',
    gap: 'gap-1',
    spacing: 'space-y-1',
  },
  comfortable: {
    padding: 'py-2.5 px-3',
    gap: 'gap-2',
    spacing: 'space-y-2',
  },
};

export function UXPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [textSize, setTextSize] = useState(() => {
    const stored = localStorage.getItem('uxTextSize');
    if (stored && TEXT_SIZE_OPTIONS.includes(stored)) {
      return stored;
    }
    return 'medium';
  });

  const [density, setDensity] = useState(() => {
    const stored = localStorage.getItem('uxDensity');
    if (stored && DENSITY_OPTIONS.includes(stored)) {
      return stored;
    }
    return 'comfortable';
  });

  useEffect(() => {
    localStorage.setItem('uxTextSize', textSize);
    document.documentElement.setAttribute('data-text-size', textSize);
  }, [textSize]);

  useEffect(() => {
    localStorage.setItem('uxDensity', density);
    document.documentElement.setAttribute('data-density', density);
  }, [density]);

  const cycleTextSize = () => {
    const currentIndex = TEXT_SIZE_OPTIONS.indexOf(textSize);
    const nextIndex = (currentIndex + 1) % TEXT_SIZE_OPTIONS.length;
    setTextSize(TEXT_SIZE_OPTIONS[nextIndex]);
  };

  const toggleDensity = () => {
    setDensity(density === 'compact' ? 'comfortable' : 'compact');
  };

  const getTextSizeClass = (customMapping: Record<string, string> | null = null) => {
    if (customMapping && customMapping[textSize]) {
      return customMapping[textSize];
    }
    return TEXT_SIZE_CLASSES[textSize];
  };

  const getDensityClasses = () => {
    return DENSITY_CLASSES[density];
  };

  return (
    <UXPreferencesContext.Provider
      value={{
        textSize,
        setTextSize,
        cycleTextSize,
        density,
        setDensity,
        toggleDensity,
        getTextSizeClass,
        getDensityClasses,
        TEXT_SIZE_OPTIONS,
        DENSITY_OPTIONS,
      }}
    >
      {children}
    </UXPreferencesContext.Provider>
  );
}

export function useUXPreferences(): UXPreferencesContextValue {
  const context = useContext(UXPreferencesContext);
  if (!context) {
    logger.warn('useUXPreferences: UXPreferencesContext not available');
    return {
      textSize: 'medium',
      setTextSize: () => {},
      cycleTextSize: () => {},
      density: 'comfortable',
      setDensity: () => {},
      toggleDensity: () => {},
      getTextSizeClass: () => 'text-base',
      getDensityClasses: () => DENSITY_CLASSES.comfortable,
      TEXT_SIZE_OPTIONS,
      DENSITY_OPTIONS,
    };
  }
  return context;
}
